// src/components/UserMeet/User-Meet-Private/LeaveBreakoutModal.jsx
import React from "react";
import { X, ArrowLeft, AlertTriangle, Users, StopCircle } from "lucide-react";

export default function LeaveBreakoutModal({
  open,
  onClose,
  onConfirmLeave,
  onEnd,
  leaving,
  isOwner,
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[10001] p-5 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl max-w-md w-full border border-yellow-500/15 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10 flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500/15 to-amber-500/15 border border-yellow-500/30 flex items-center justify-center flex-shrink-0">
              <ArrowLeft className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-1">
                Leave Breakout Room?
              </h2>
              <p className="text-sm text-white/60">
                You're about to return to the main meeting
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={leaving}
            className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-red-500/15 hover:border-red-500/30 hover:text-red-400 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Warning Card */}
          <div className="flex gap-3 p-4 bg-yellow-500/8 border border-yellow-500/20 rounded-xl">
            <AlertTriangle
              size={20}
              className="text-yellow-400 flex-shrink-0 mt-0.5"
            />
            <div>
              <p className="text-sm font-semibold text-yellow-400 mb-1">
                You won't be able to rejoin
              </p>
              <p className="text-sm text-white/60 leading-relaxed">
                Once you leave this breakout room, you{" "}
                <span className="text-white font-semibold">
                  won't be able to join back
                </span>{" "}
                unless the owner sends you a new invitation.
              </p>
            </div>
          </div>

          {/* What happens */}
          <div className="space-y-2.5">
            <p className="text-xs font-bold text-white/40 uppercase tracking-wide">
              What will happen
            </p>
            {[
              "You will be returned to the main meeting",
              "The breakout room will continue without you",
              "Other participants will remain in the breakout",
              "You'll need a new invite to rejoin",
            ].map((item) => (
              <div key={item} className="flex items-start gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/70 mt-1.5 flex-shrink-0" />
                <p className="text-sm text-white/60">{item}</p>
              </div>
            ))}
          </div>

          {/* End breakout tip for owners */}
          {isOwner && (
            <div className="flex gap-3 p-3 bg-red-500/8 border border-red-500/20 rounded-xl">
              <StopCircle
                size={16}
                className="text-red-400 flex-shrink-0 mt-0.5"
              />
              <p className="text-xs text-white/60 leading-relaxed">
                <span className="text-red-400 font-semibold">
                  Want to end the entire breakout?
                </span>{" "}
                Use the{" "}
                <span className="text-white font-semibold">"End"</span>{" "}
                button in the breakout controls instead. That will close the
                room for everyone.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-white/10 flex items-center gap-3">
          <button
            onClick={onClose}
            disabled={leaving}
            type="button"
            className="flex-1 px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white/80 font-semibold text-sm hover:bg-white/10 hover:border-white/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Stay in Breakout
          </button>
          <button
            onClick={onConfirmLeave}
            disabled={leaving}
            type="button"
            className="flex-1 px-5 py-2.5 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 border border-yellow-500/50 text-white font-semibold text-sm hover:from-amber-500 hover:to-yellow-600 hover:shadow-lg hover:shadow-yellow-500/30 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {leaving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Leaving...
              </>
            ) : (
              <>
                <ArrowLeft size={16} />
                Leave to Main
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}